import {
  REVIEWABLE_DOCUMENT_TYPES,
  ReviewableDocumentInput,
} from '@bordrio/shared';
import {
  Control,
  FieldPath,
  FieldPathValue,
  FieldValues,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HiCheckCircle } from 'react-icons/hi';
import ReviewableDocumentFileInput from '../ReviewableDocumentFileInput/ReviewableDocumentFileInput';

interface Props<
  ValueType extends FieldValues,
  PathType extends FieldPath<ValueType>,
> {
  className?: string;
  control: FieldPathValue<ValueType, PathType> extends ReviewableDocumentInput
    ? Control<ValueType>
    : never;
  name: PathType;
}

const INSTRUCTIONS = [
  // t('translation:proof_of_identity_input.instructions.corners', 'Shows all 4 corners')
  'translation:proof_of_identity_input.instructions.corners',
  // t('translation:proof_of_identity_input.instructions.signed', 'Document is signed')
  'translation:proof_of_identity_input.instructions.signed',
  // t('translation:proof_of_identity_input.instructions.legible', 'All details are fully legible')
  'translation:proof_of_identity_input.instructions.legible',
];

function ProofOfIdentityDocumentInput<
  ValueType extends FieldValues,
  PathType extends FieldPath<ValueType>,
>({ name, control }: Props<ValueType, PathType>): JSX.Element {
  const { t } = useTranslation();
  const manualTranslate = t;

  // a hack to allow proper typings for the control
  const controlTyped = control as unknown as Control<{
    prefix: ReviewableDocumentInput;
  }>;
  const prefix = name as 'prefix';

  const documentTypeId = useWatch({
    control: controlTyped,
    name: `${prefix}.currentVersion.documentTypeId`,
  });

  const isEuId = REVIEWABLE_DOCUMENT_TYPES.eu_id_card.id === documentTypeId;

  return (
    <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
      <div className="order-1 sm:order-none">
        <ReviewableDocumentFileInput control={control} name={name} />
      </div>

      <div>
        <div className="mt-3 flex items-center space-x-4">
          <img
            className="w-40"
            src={isEuId ? '/images/eu_id.jpg' : '/images/passport.jpg'}
            alt={
              isEuId
                ? t(
                    'proof_of_identity_input.example_eu_id_alt',
                    'Example EU ID',
                  )
                : t(
                    'proof_of_identity_input.example_passport_alt',
                    'Example Passport',
                  )
            }
          />
          <div className="space-y-4">
            <h3>
              {t(
                'proof_of_identity_input.passport_instructions.title',
                'Good example',
              )}
            </h3>
            <div className="space-y-2">
              {INSTRUCTIONS.map((instruction) => (
                <div className="flex items-center space-x-2" key={instruction}>
                  <HiCheckCircle className="h-5 w-5 shrink-0 text-green-500" />
                  <div className="text-xs font-medium leading-4">
                    {manualTranslate(instruction)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProofOfIdentityDocumentInput;
